import { Button } from '@crowdfpl/ui-web/button';
import { Input } from '@crowdfpl/ui-web/input';
import { Check, ChevronDown } from 'lucide-react';
import { useState, useMemo, useRef, useEffect } from 'react';
import { getCountryOptions, getCountryFlag, getCountryName, searchCountries } from '@/helpers/countries';
import { cn } from '@/helpers/utils';

interface CountrySelectorProps {
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  isDetecting?: boolean;
  placeholder?: string;
  className?: string;
}

export function CountrySelector({
  value,
  onChange,
  disabled = false,
  isDetecting = false,
  placeholder = 'Select country',
  className,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const countries = useMemo(() => {
    return search ? searchCountries(search) : getCountryOptions();
  }, [search]);

  const selectedCountry = value ? {
    code: value,
    name: getCountryName(value),
    flag: getCountryFlag(value),
  } : null;

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
    setSearch('');
  };

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Focus input when open and scroll into view (mobile keyboard fix)
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      // Delay to let keyboard animate - then scroll input into visible area
      const timer = setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled || isDetecting}
        onClick={() => setOpen(!open)}
        className="w-full min-w-[240px] justify-between font-normal h-10"
      >
        {isDetecting ? (
          <span className="text-muted-foreground">Detecting...</span>
        ) : selectedCountry ? (
          <span className="flex items-center gap-2">
            <span>{selectedCountry.flag}</span>
            <span className="truncate">{selectedCountry.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
      </Button>

      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 w-full min-w-[240px] rounded-md border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95">
          <div className="p-2 border-b border-border">
            <Input
              ref={inputRef}
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {countries.length === 0 ? (
              <div className="py-3 px-2 text-sm text-muted-foreground text-center">
                No country found
              </div>
            ) : (
              countries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => handleSelect(country.code)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left hover:bg-accent transition-colors',
                    value === country.code && 'bg-accent',
                  )}
                >
                  <span>{country.flag}</span>
                  <span className="flex-1 truncate">{country.name}</span>
                  {value === country.code && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact country display (for viewing, not editing)
 */
export function CountryDisplay({ code, className }: { code: string; className?: string }) {
  return (
    <div className={cn('flex items-center gap-1.5 text-sm text-muted-foreground', className)}>
      <span>{getCountryFlag(code)}</span>
      <span>{getCountryName(code)}</span>
    </div>
  );
}
